/* eslint-disable max-lines-per-function */
/**
 * @expFlag public-app-download-banner
 * @ticket CLOUDWEB-20131
 * @file
 */
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { abPromoInWebviewPublic, PROMO_ID } from 'reactApp/appHelpers/featuresHelpers/features/promoInWebviewPublic';
import {
    abPublicAppDownloadBannerTouchSelector,
    publicAppDownloadBannerTouch,
    publicAppDownloadBannerTouchSafari,
} from 'reactApp/appHelpers/featuresHelpers/features/publicAppDownloadBannerTouch';
import { chooseVariant } from 'reactApp/appHelpers/featuresHelpers/utils';
import { getPublicItemType } from 'reactApp/modules/public/public.helpers';
import { getCurrentPublicItem } from 'reactApp/modules/public/public.selectors';
import { sendRedirectPromoElementAnalytics } from 'reactApp/modules/redirectToApp/redirectToApp.analytics';
import { ERedirectPromoElement } from 'reactApp/modules/redirectToApp/redirectToApp.constants';
import { canShowPromo, recordPromoShown } from 'reactApp/modules/redirectToApp/redirectToApp.helpers';
import { hideSnackbarAction, setZIndexAction, showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { sendDwh as sendPublicDwh } from 'reactApp/utils/ga';
import opener from 'reactApp/utils/opener';
import { ECategoryGa } from 'reactApp/utils/paymentGa';

const DOWNLOAD_APP_SNAKCBAR_ID = 'download-app';
const SNACKBAR_STORAGE_KEY = `${PROMO_ID}-snackbar-c-last-shown`;

export const usePublicSnackbar = (showSnackbar: boolean, isSelecting: boolean, zIndex?: number) => {
    const dispatch = useDispatch();
    const publicItem = useSelector(getCurrentPublicItem);
    const wasSnackbarShownRef = useRef(false);
    const userClosedSnackbarRef = useRef(false);
    let canShowSnackbar = showSnackbar;

    const deepLink = `?mt_deeplink=${window.location.href}`;
    let link = publicAppDownloadBannerTouch ? publicAppDownloadBannerTouch.href : '';
    link =
        publicAppDownloadBannerTouchSafari && publicAppDownloadBannerTouchSafari.type === 'b'
            ? publicAppDownloadBannerTouchSafari.href
            : link;
    let snackbarText: string = '';
    let buttonText = 'Скачать Облако';
    snackbarText = chooseVariant(() => abPublicAppDownloadBannerTouchSelector(), {
        control: '',
        variant2: 'В приложении — файлы по папкам',
        variant3: 'Всё удобнее — в приложении',
    });

    /**
     * @expFlag promo-in-webview-public
     * @ticket CLOUDWEB-20374
     * @nextline
     */
    if (abPromoInWebviewPublic && abPromoInWebviewPublic.type === 'c') {
        snackbarText = 'Приложение Облака сохранит фото, чтобы вы могли освободить телефон';
        buttonText = 'Скачать приложение';
        canShowSnackbar = showSnackbar && canShowPromo(SNACKBAR_STORAGE_KEY);
    }

    const handleOnMobileAppClick = useCallback(() => {
        /**
         * @expFlag promo-in-webview-public
         * @ticket CLOUDWEB-20374
         * @nextline
         */
        if (abPromoInWebviewPublic && abPromoInWebviewPublic.type === 'c') {
            recordPromoShown(SNACKBAR_STORAGE_KEY);
            sendRedirectPromoElementAnalytics({
                trkLink: abPromoInWebviewPublic.href,
                promoElement: ERedirectPromoElement.snackbar,
                action: 'click',
            });
        } else {
            sendPublicDwh({
                eventCategory: ECategoryGa.publicActionToInstall,
                action: 'click-download',
                dwhData: {
                    type_public: publicItem ? getPublicItemType(publicItem) : undefined,
                    type_element: 'snackbar',
                },
            });
        }

        setTimeout(() => {
            if (abPromoInWebviewPublic && abPromoInWebviewPublic.type === 'c') {
                opener(abPromoInWebviewPublic.href + deepLink);
            } else {
                window.location.href = link + deepLink;
            }
        }, 100);
    }, [publicItem, deepLink, link]);

    useEffect(() => {
        if (zIndex !== undefined) {
            dispatch(setZIndexAction(zIndex));
        }
        return () => {
            dispatch(setZIndexAction(undefined));
        };
    }, [zIndex, dispatch]);

    const handleOnClose = useCallback(() => {
        if (userClosedSnackbarRef.current) {
            return;
        }
        userClosedSnackbarRef.current = true;
        /**
         * @expFlag promo-in-webview-public
         * @ticket CLOUDWEB-20374
         * @nextline
         */
        if (abPromoInWebviewPublic && abPromoInWebviewPublic.type === 'c') {
            recordPromoShown(SNACKBAR_STORAGE_KEY);
            sendRedirectPromoElementAnalytics({
                trkLink: abPromoInWebviewPublic.href,
                promoElement: ERedirectPromoElement.snackbar,
                action: 'close',
            });
        } else {
            sendPublicDwh({
                eventCategory: ECategoryGa.publicActionToInstall,
                action: 'close',
                dwhData: {
                    type_public: publicItem ? getPublicItemType(publicItem) : undefined,
                    type_element: 'snackbar',
                },
            });
        }
    }, [publicItem]);

    useEffect(() => {
        if (canShowSnackbar && publicItem) {
            if (isSelecting || userClosedSnackbarRef.current) {
                // Если идет выбор или пользователь закрыл его, скрываем
                dispatch(hideSnackbarAction(DOWNLOAD_APP_SNAKCBAR_ID));
                /**
                 * @expFlag promo-in-webview-public
                 * @ticket CLOUDWEB-20374
                 * @nextline
                 */
                if (!(abPromoInWebviewPublic && abPromoInWebviewPublic.type === 'c')) {
                    wasSnackbarShownRef.current = false;
                }
                return;
            }

            const showSnackbarActionObj = showSnackbarAction({
                id: DOWNLOAD_APP_SNAKCBAR_ID,
                type: SnackbarTypes.mobileAppDownload,
                text: snackbarText,
                iconSize: 'flex',
                closable: true,
                disableCloseTimeout: true,
                buttonText,
                onButtonClick: handleOnMobileAppClick,
                onClose: handleOnClose,
            });

            if (!wasSnackbarShownRef.current) {
                /**
                 * @expFlag promo-in-webview-public
                 * @ticket CLOUDWEB-20374
                 * @nextline
                 */
                if (abPromoInWebviewPublic && abPromoInWebviewPublic.type === 'c') {
                    sendRedirectPromoElementAnalytics({
                        trkLink: abPromoInWebviewPublic.href,
                        promoElement: ERedirectPromoElement.snackbar,
                        action: 'show',
                    });
                } else {
                    sendPublicDwh({
                        eventCategory: ECategoryGa.publicActionToInstall,
                        action: 'show',
                        dwhData: {
                            type_public: publicItem ? getPublicItemType(publicItem) : undefined,
                            type_element: 'snackbar',
                        },
                    });
                }
                wasSnackbarShownRef.current = true;
            }

            dispatch(showSnackbarActionObj);

            return () => {
                dispatch(hideSnackbarAction(DOWNLOAD_APP_SNAKCBAR_ID));
            };
        }
    }, [canShowSnackbar, buttonText, isSelecting, dispatch, handleOnMobileAppClick, publicItem, snackbarText, handleOnClose]);
};
